import type { LinkFieldValue } from '@core/jss-models';
import type { LinkField } from '@core/jss-proxy';

/**
 * Normalizes a Sitecore link field to a safe, usable href.
 * Handles tel:, mailto:, http(s), and internal links — and
 * fixes accidental prefixes like https://tel:+31123456789.
 */
export function normalizeLink(link?: LinkFieldValue): LinkFieldValue {
  if (!link || typeof link['url'] !== 'string') {
    return link ?? {};
  }

  let raw = link['url'].trim();

  // Fix malformed cases like "https://tel:+31123456789"
  raw = raw.replace(/^(https?:\/\/)(tel:|mailto:)/i, '$2');

  // Normalize protocol to lowercase (e.g., TEL: → tel:)
  raw = raw.replace(/^(TEL:|MAILTO:)/i, (m) => m.toLowerCase());

  // Recognize allowed custom protocols
  const allowedSchemes = ['tel:', 'mailto:'];
  if (allowedSchemes.some((scheme) => raw.toLowerCase().startsWith(scheme))) {
    return { ...link, href: raw };
  }

  // Starts with http/https or another valid scheme
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
    return { ...link, href: raw };
  }

  // Internal link or path (e.g. "/contact" or "contact-us")
  if (raw.startsWith('/')) {
    return { ...link, href: raw };
  }

  // Domain without protocol (e.g. "example.com")
  if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(raw)) {
    return { ...link, href: `https://${raw}` };
  }

  // Fallback: assume it’s a relative path
  return { ...link, href: `/${raw}` };
}

export function normalizeLinkField(
  linkField?: LinkField
): LinkField | undefined {
  if (!linkField) {
    return undefined;
  }

  const value = linkField.value;

  return {
    ...linkField,
    value: value ? normalizeLink(value) : value,
  };
}
